// SandboxBanner.tsx — Phase 2.B
//
// Persistent banner shown across the app when the practice is in a pre-BAA
// status (sandbox/onboarding/pending_baa). Reminds the user that real PHI
// cannot be entered until they activate, and gives them a one-click CTA.
//
// Mounted globally in AppShell. Reads practice.status from useAuthStore.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../store';
import { canConfigureCurrentPractice } from '../../lib/gating';
import type { PracticeStatus } from '../../types';

const PRE_BAA_STATUSES: PracticeStatus[] = ['onboarding', 'sandbox', 'pending_baa'];

// ai.359 (F12, R5) - how the strip ends for a viewer whose door refuses its CTA (/onboarding and /activate admit only
// canConfigurePractice): plain text, no button. PA-F6 / PA-F17c (D-2026-10-23-04) - one spelling, "practice
// administrator"; the pending-BAA ending says the activation is under way; onboarding has no ending (its staff label
// already says it), so the ending span renders only for a status listed here.
const NON_CONFIGURER_ENDING: Record<string, string> = {
  sandbox: 'Ask your practice administrator to activate.',
  pending_baa: 'Your practice administrator is finishing activation.',
};

// PA-F6 (D-2026-10-23-04) - the label a non-configurer reads: no "you" action and no price. A status not listed here
// (pending_baa) keeps its STATUS_COPY label for every viewer.
const NON_CONFIGURER_LABEL: Record<string, string> = {
  onboarding: 'Your practice administrator is finishing setup.',
  sandbox: 'This practice is in Sandbox mode with demo patients; real patient data unlocks when your practice is activated.',
};

const STATUS_COPY: Record<string, { label: string; cta: string; tone: 'warning' | 'info' }> = {
  onboarding: {
    label: 'Your onboarding wizard is incomplete. Real patient data is blocked until you finish setup and activate.',
    cta: 'Resume onboarding',
    tone: 'warning',
  },
  sandbox: {
    label: 'You\'re in Sandbox mode. Explore RCMTask with demo patients — real patient data is blocked until you activate ($250).',
    cta: 'Activate to use real patients',
    tone: 'info',
  },
  pending_baa: {
    label: 'Your activation request is in progress. Once your BAA is signed, you\'ll be able to enter real patient data.',
    cta: 'View activation status',
    tone: 'info',
  },
};

export default function SandboxBanner() {
  const navigate = useNavigate();
  const { currentPractice } = useAuthStore();
  const status = currentPractice?.status as PracticeStatus | undefined;

  if (!status || !PRE_BAA_STATUSES.includes(status)) {
    return null;
  }

  const copy = STATUS_COPY[status];
  if (!copy) return null;

  // One predicate read per render, for both the label and the CTA, so the two can never disagree.
  const canConfigure = canConfigureCurrentPractice();

  const baseClass =
    copy.tone === 'warning'
      ? 'bg-amber-50 border-b border-amber-200 text-amber-900'
      : 'bg-[#F4F1EE] border-b border-[#C94B1F]/20 text-[#A8390F]';

  const handleCta = () => {
    if (status === 'onboarding') navigate('/onboarding');
    else navigate('/activate'); // Phase 2.E will own this route
  };

  return (
    <div className={`${baseClass} px-4 py-2 flex items-center gap-3 text-sm`}>
      <AlertTriangle aria-hidden="true" className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 leading-snug">
        <strong className="font-semibold uppercase tracking-wide text-xs mr-2">
          {status === 'onboarding' ? 'Setup incomplete' : status === 'sandbox' ? 'Sandbox mode' : 'Pending BAA'}
        </strong>
        {canConfigure ? copy.label : (NON_CONFIGURER_LABEL[status] ?? copy.label)}
      </span>
      {canConfigure ? (
        <button
          onClick={handleCta}
          className="flex items-center gap-1 text-xs font-semibold underline hover:no-underline whitespace-nowrap"
        >
          {copy.cta} <ArrowRight aria-hidden="true" className="w-3 h-3" />
        </button>
      ) : NON_CONFIGURER_ENDING[status] ? (
        <span className="text-xs font-semibold whitespace-nowrap">{NON_CONFIGURER_ENDING[status]}</span>
      ) : null}
    </div>
  );
}

/**
 * Helper: returns true if the current practice is in a state where real PHI
 * cannot be entered. UI components can use this to disable inputs / show
 * "DEMO ONLY" labels on PHI forms.
 */
export function useIsSandboxMode(): boolean {
  const { currentPractice } = useAuthStore();
  const status = currentPractice?.status as PracticeStatus | undefined;
  return !!status && PRE_BAA_STATUSES.includes(status);
}

/**
 * Inline label component shown on PHI forms when in sandbox mode. Renders
 * nothing in active/enterprise. Drop into any patient/appointment/task form.
 */
export function DemoOnlyLabel({ className = '' }: { className?: string }) {
  const isSandbox = useIsSandboxMode();
  if (!isSandbox) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded ${className}`}
    >
      Demo only
    </span>
  );
}
